import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  FAQ_OPTIMIZE_MIN_NOT_HELPFUL,
  FAQ_OPTIMIZE_MIN_NOT_HELPFUL_MAX,
  FAQ_OPTIMIZE_RATE_MAX,
  FAQ_OPTIMIZE_RATE_MIN,
  FAQ_OPTIMIZE_RATE_THRESHOLD,
} from "@/pages/help/faqData";

const SCOPE = "default";

interface UseFaqSettingsResult {
  rateThreshold: number;
  minNotHelpful: number;
  loading: boolean;
  saving: boolean;
  save: (rateThreshold: number, minNotHelpful: number, editor: string) => Promise<void>;
  reload: () => void;
}

function clamp(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

/**
 * 读取 / 保存「待优化」阈值设置（占比与最小没帮助条数），让运营自定义判定标准。
 * 读写失败时回落到内置默认值，不影响页面展示。
 */
export function useFaqSettings(): UseFaqSettingsResult {
  const [rateThreshold, setRateThreshold] = useState(FAQ_OPTIMIZE_RATE_THRESHOLD);
  const [minNotHelpful, setMinNotHelpful] = useState(FAQ_OPTIMIZE_MIN_NOT_HELPFUL);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("faq_settings")
        .select("rate_threshold, min_not_helpful")
        .eq("scope", SCOPE)
        .maybeSingle();
      if (error) throw error;
      if (data) {
        const row = data as { rate_threshold: number | null; min_not_helpful: number | null };
        setRateThreshold(
          clamp(Number(row.rate_threshold), FAQ_OPTIMIZE_RATE_MIN, FAQ_OPTIMIZE_RATE_MAX, FAQ_OPTIMIZE_RATE_THRESHOLD),
        );
        setMinNotHelpful(
          clamp(
            Number(row.min_not_helpful),
            0,
            FAQ_OPTIMIZE_MIN_NOT_HELPFUL_MAX,
            FAQ_OPTIMIZE_MIN_NOT_HELPFUL,
          ),
        );
      }
    } catch {
      /* keep defaults */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(async (nextRate: number, nextMin: number, editor: string) => {
    const safeRate = clamp(nextRate, FAQ_OPTIMIZE_RATE_MIN, FAQ_OPTIMIZE_RATE_MAX, FAQ_OPTIMIZE_RATE_THRESHOLD);
    const safeMin = clamp(nextMin, 0, FAQ_OPTIMIZE_MIN_NOT_HELPFUL_MAX, FAQ_OPTIMIZE_MIN_NOT_HELPFUL);
    setSaving(true);
    const prevRate = rateThreshold;
    const prevMin = minNotHelpful;
    setRateThreshold(safeRate);
    setMinNotHelpful(safeMin);
    try {
      const { error } = await supabase.from("faq_settings").upsert(
        {
          scope: SCOPE,
          rate_threshold: safeRate,
          min_not_helpful: safeMin,
          updated_at: new Date().toISOString(),
          updated_by: editor || null,
        },
        { onConflict: "scope" },
      );
      if (error) throw error;
    } catch {
      setRateThreshold(prevRate);
      setMinNotHelpful(prevMin);
    } finally {
      setSaving(false);
    }
  }, [rateThreshold, minNotHelpful]);

  return { rateThreshold, minNotHelpful, loading, saving, save, reload: load };
}